import { supabase } from '../lib/supabase';
import { checkDaysOf } from '../lib/libraryStates';
import type { BookData, Copy, Edition, HistoryRow, Loan, Note, Quote, Read, SeriesBook, SeriesPlace, SimilarBook, Status } from './types';
import { sortHonours } from '../discover/lists';

// Loads one book on your list for the book page. Plain, one-level queries in three rounds,
// each read with your own sign-in (the row rules show only your own rows and shared facts).

type Row = Record<string, unknown>;

/** A one-to-one embed comes back as an object or a one-item list. */
const one = <T>(x: unknown): T | null => (Array.isArray(x) ? ((x[0] as T) ?? null) : ((x as T) ?? null));
const many = <T>(x: unknown): T[] => (Array.isArray(x) ? (x as T[]) : x ? [x as T] : []);
const num = (x: unknown) => (x === null || x === undefined ? null : Number(x));

async function rows(q: PromiseLike<{ data: unknown; error: { message: string } | null }>): Promise<Row[]> {
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data as Row[] | null) ?? [];
}

export class BookNotFound extends Error {}

/** A book's authors for a tile: the first, "and others" when there are more. */
const firstAuthorOf = (people: Row[] | undefined, workId: unknown): string | null => {
  const names = (people ?? [])
    .filter((p) => p.work_id === workId && (p.role === 'author' || p.role === 'co_author'))
    .sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0))
    .map((p) => (one<Row>(p.person)?.name as string) ?? '')
    .filter(Boolean);
  return names.length ? names[0] + (names.length > 1 ? ' and others' : '') : null;
};

/** A copy of your own you have now (not a library loan): the book page's Owned rule. Unknown (no copies loaded) counts as owned. */
const ownsOne = (copies: Row[] | undefined, bookId: unknown) =>
  copies === undefined || copies.some((c) => c.book_id === bookId && c.how !== 'library_loan' && c.state === 'have_it');

export async function loadBook(label: number): Promise<BookData> {
  const db = supabase;
  if (!db) throw new Error('Not connected to a database.');

  // Round 1: the book and its catalog work.
  const head = await rows(db.from('books').select('*, work:works(*), read_type:read_types(name, description)').eq('label_number', label).limit(1));
  const b = head[0];
  // A removed book (Remove this book, 0030) is off your list, though Undo can bring it back for a day.
  if (!b || b.removed_at) throw new BookNotFound(`No book ${label} on your list.`);
  const work = one<Row>(b.work)!;
  const bookId = b.id as string;
  const workId = b.work_id as string;

  // Round 2: everything hung on the book, the work and the reads.
  const [priv, people, places, editions, copies, reads, genres, subgenres, tags, notes, quotes, history, links, lists, libraries, readTypes, positions, leftOut, honours] = await Promise.all([
    rows(db.from('book_private').select('why_read, recommended_by, notes').eq('book_id', bookId)),
    rows(db.from('work_people').select('role, sort_order, person_id, person:people(name)').eq('work_id', workId).order('sort_order')),
    rows(db.from('series_works').select('series_id, position, label, series:series(name)').eq('work_id', workId)),
    rows(
      db
        .from('editions')
        .select('id, kind, title, publisher, imprint, edition_statement, published_on, published_precision, pages, listening_minutes, language, isbn13, cover_url, asin, audible_id, edition_people(role, sort_order, person:people(name))')
        .eq('work_id', workId),
    ),
    rows(db.from('copies').select('*, store:stores(name), copy_private(acquired_words, note, price, found_through, got_from, shelf_name, lent_to, lent_on, room:home_locations(name))').eq('book_id', bookId)),
    rows(
      db
        .from('reads')
        .select(
          '*, read_private(started_words, ended_words, stop_reason), read_copies(copy_id, added_on, stopped_using_on), pauses(paused_on, resumed_on, chips, pause_private(reason)), progress_updates(noted_at, percent), takeaways(body, sort_order)',
        )
        .eq('book_id', bookId),
    ),
    rows(db.from('book_genres').select('sort_order, genre:genres(name, short_name, hidden)').eq('book_id', bookId).order('sort_order')),
    rows(db.from('book_subgenres').select('sort_order, subgenre_id, subgenre:subgenres(name, short_name)').eq('book_id', bookId).order('sort_order')),
    rows(db.from('book_tags').select('tag:tags(name, sort_order)').eq('book_id', bookId)),
    rows(db.from('reading_notes').select('id, noted_on, body, location, quote_id, source, read_id, from_removed_read').eq('book_id', bookId)),
    rows(db.from('quotes').select('id, body, location, chapter, quoted_on, source, read_id, created_at, from_removed_read').eq('book_id', bookId)),
    rows(db.from('history').select('id, happened_at, kind, from_status, to_status, place, read_id').eq('book_id', bookId).order('happened_at', { ascending: false })),
    rows(db.from('links').select('copy_id, kind, url, label').eq('book_id', bookId)),
    rows(db.from('my_lists_and_clubs').select('name, year').eq('book_id', bookId)),
    rows(db.from('libraries').select('libby_code, sort_order').order('sort_order').limit(1)),
    rows(db.from('read_types').select('name, description, sort_order').order('sort_order')),
    rows(db.from('book_positions').select('shelf').eq('book_id', bookId)),
    rows(db.from('kindle_left_out').select('kindle_key').eq('book_id', bookId)),
    // The catalog's lists the work is on (n127): Awards & lists under What others say.
    rows(db.from('list_entries').select('level, category, rank, edition:list_editions(year, label, list:lists(id, name, sort_order))').eq('work_id', workId)),
  ]);

  // Round 3: the library copies' loans, the other books of the series, and books sharing a subgenre.
  const copyIds = copies.filter((c) => c.how === 'library_loan').map((c) => c.id as string);
  const seriesIds = places.map((p) => p.series_id as string);
  const subgenreIds = subgenres.map((g) => g.subgenre_id as string).filter(Boolean);
  const none = Promise.resolve([] as Row[]);
  // Links saved on a copy (Add a copy…, edit mode) carry the copy, not the book.
  const allCopyIds = copies.map((c) => c.id as string);
  // The book's authors, for More by the author on your list (Kristi, Oct 28).
  const authorIds = people.filter((p) => p.role === 'author' || p.role === 'co_author').map((p) => p.person_id as string).filter(Boolean);
  const [loans, seriesWorks, subgenreMates, copyLinks, authorWorks] = await Promise.all([
    copyIds.length
      ? rows(db.from('library_loans').select('*, library:libraries(name, libby_code, check_days_before), library_events(id, kind, happened_on, due_on, percent)').in('copy_id', copyIds))
      : none,
    seriesIds.length ? rows(db.from('series_works').select('series_id, work_id, position').in('series_id', seriesIds)) : none,
    subgenreIds.length ? rows(db.from('book_subgenres').select('book_id, subgenre_id').in('subgenre_id', subgenreIds).neq('book_id', bookId).limit(300)) : none,
    allCopyIds.length ? rows(db.from('links').select('id, copy_id, kind, url, label').in('copy_id', allCopyIds)) : none,
    authorIds.length
      ? rows(db.from('work_people').select('work_id').in('person_id', authorIds).in('role', ['author', 'co_author']).neq('work_id', workId).limit(200))
      : none,
  ]);
  const allLinks = [...links, ...copyLinks.filter((l) => !links.some((x) => x.copy_id === l.copy_id && x.url === l.url))];
  const otherWorks = [...new Set(seriesWorks.map((s) => s.work_id as string).filter((w) => w !== workId))];
  const mateIds = [...new Set(subgenreMates.map((m) => m.book_id as string))].slice(0, 300);

  // Round 4: those books (only Up Next and Future reads for the subgenre row).
  const byAuthorWorks = [...new Set(authorWorks.map((a) => a.work_id as string))].filter((w) => !otherWorks.includes(w));
  const [seriesBooks, similarRows, authorRows] = await Promise.all([
    otherWorks.length ? rows(db.from('books').select('id, label_number, status, work_id, work:works(title)').in('work_id', otherWorks)) : none,
    mateIds.length
      ? rows(db.from('books').select('id, label_number, status, work_id, work:works(title)').in('id', mateIds).in('status', ['up_next', 'future_reads']))
      : none,
    byAuthorWorks.length ? rows(db.from('books').select('id, label_number, status, work_id, work:works(title)').in('work_id', byAuthorWorks).is('removed_at', null)) : none,
  ]);
  const authorBooks = pickByAuthor(authorRows);
  const similarBooks = pickSimilar(similarRows, subgenreMates, new Set([...otherWorks, ...authorBooks.map((a) => a.work_id as string)]));

  // Round 5: covers for both rows.
  const coverWorks = [...otherWorks, ...authorBooks.map((a) => a.work_id as string), ...similarBooks.map((s) => s.work_id as string)];
  // With their copies, so each can say Not owned yet (Kristi, Oct 28), by the book page's rule.
  const otherBookIds = [...seriesBooks, ...authorBooks, ...similarBooks].map((x) => x.id as string).filter(Boolean);
  const [covers, otherCopies, otherPeople] = await Promise.all([
    coverWorks.length ? rows(db.from('editions').select('work_id, cover_url').in('work_id', coverWorks).not('cover_url', 'is', null)) : none,
    otherBookIds.length ? rows(db.from('copies').select('book_id, how, state').in('book_id', otherBookIds)) : none,
    // Their authors, for the tiles (title, then the author underneath; Kristi, Oct 28).
    coverWorks.length ? rows(db.from('work_people').select('work_id, role, sort_order, person:people(name)').in('work_id', coverWorks)) : none,
  ]);

  return toBookData({
    b, work, priv, people, places, editions, copies, reads, genres, subgenres, tags, notes, quotes, history, links: allLinks, lists, libraries, loans, readTypes, positions, leftOut, honours,
    seriesWorks, seriesBooks, seriesCovers: covers, similarBooks, subgenreMates, otherCopies, otherPeople, authorBooks,
  });
}

/**
 * More like this › Same subgenre (Kristi, Oct 26): up to 6 books on your list sharing a subgenre,
 * Up Next first, then Future reads; most subgenres in common first; series books left out.
 */
/** More by the author on your list: up to 6, reading and queued first, then finished, then the rest. */
export function pickByAuthor(books: Row[], max = 6): Row[] {
  const order: (Status | null)[] = ['currently_reading', 'up_next', 'future_reads', 'finished', null, 'reference', 'not_for_me'];
  return [...books].sort((a, b) => order.indexOf((a.status as Status) ?? null) - order.indexOf((b.status as Status) ?? null) || Number(a.label_number) - Number(b.label_number)).slice(0, max);
}

export function pickSimilar(books: Row[], mates: Row[], seriesWorks: Set<string>, max = 6): Row[] {
  const shared = (id: unknown) => mates.filter((m) => m.book_id === id).length;
  const rank = (st: unknown) => (st === 'up_next' ? 0 : st === 'future_reads' ? 1 : 2);
  return books
    .filter((x) => rank(x.status) < 2 && !seriesWorks.has(x.work_id as string))
    .sort((a, c) => rank(a.status) - rank(c.status) || shared(c.id) - shared(a.id) || Number(a.label_number) - Number(c.label_number))
    .slice(0, max);
}

/** Turns the rows into the page's data (kept apart from the queries so tests can feed it). */
export function toBookData(r: {
  b: Row;
  work: Row;
  priv: Row[];
  people: Row[];
  places: Row[];
  editions: Row[];
  copies: Row[];
  reads: Row[];
  genres: Row[];
  subgenres: Row[];
  tags: Row[];
  notes: Row[];
  quotes: Row[];
  history: Row[];
  leftOut?: Row[];
  honours?: Row[];
  links: Row[];
  lists: Row[];
  libraries: Row[];
  readTypes?: Row[];
  positions?: Row[];
  loans: Row[];
  seriesWorks: Row[];
  seriesBooks: Row[];
  seriesCovers?: Row[];
  similarBooks?: Row[];
  /** Copies of the More like this books (book_id, how, state). */
  otherCopies?: Row[];
  /** Authors of the More like this books (work_id, role, sort_order, person.name). */
  otherPeople?: Row[];
  /** Other books by the book's authors on your list. */
  authorBooks?: Row[];
  subgenreMates?: Row[];
}): BookData {
  const { b, work } = r;
  const p = r.priv[0] ?? {};
  const rt = one<Row>(b.read_type);
  const name = (x: unknown) => (one<Row>(x)?.name as string | undefined) ?? '';

  const editions: Edition[] = r.editions.map((e) => ({
    id: e.id as string,
    kind: e.kind as Edition['kind'],
    title: (e.title as string) ?? null,
    publisher: (e.publisher as string) ?? null,
    imprint: (e.imprint as string) ?? null,
    edition_statement: (e.edition_statement as string) ?? null,
    published_on: (e.published_on as string) ?? null,
    published_precision: (e.published_precision as Edition['published_precision']) ?? null,
    pages: num(e.pages),
    listening_minutes: num(e.listening_minutes),
    language: (e.language as string) ?? null,
    isbn13: (e.isbn13 as string) ?? null,
    cover_url: (e.cover_url as string) ?? null,
    asin: (e.asin as string) ?? null,
    audible_id: (e.audible_id as string) ?? null,
    narrators: many<Row>(e.edition_people)
      .filter((x) => x.role === 'narrator')
      .sort((x, y) => Number(x.sort_order) - Number(y.sort_order))
      .map((x) => name(x.person))
      .filter(Boolean),
  }));

  const loansFor = (copyId: string): Loan[] =>
    r.loans
      .filter((l) => l.copy_id === copyId)
      .map((l) => {
        const lib = one<Row>(l.library);
        return {
          id: l.id as string,
          library: (lib?.name as string) ?? null,
          libby_code: (lib?.libby_code as string) ?? null,
          check_days: checkDaysOf(lib?.check_days_before),
          step: l.step as Loan['step'],
          hold_placed_on: (l.hold_placed_on as string) ?? null,
          expected_on: (l.expected_on as string) ?? null,
          quiet_until: (l.quiet_until as string) ?? null,
          ready_on: (l.ready_on as string) ?? null,
          borrow_by: (l.borrow_by as string) ?? null,
          borrowed_on: (l.borrowed_on as string) ?? null,
          loan_days: num(l.loan_days),
          due_on: (l.due_on as string) ?? null,
          returned_on: (l.returned_on as string) ?? null,
          cancelled_on: (l.cancelled_on as string) ?? null,
          place_in_line: num(l.place_in_line),
          library_copies: num(l.library_copies),
          created_at: (l.created_at as string) ?? '',
          events: many<Row>(l.library_events).map((e) => ({
            id: e.id as string,
            kind: e.kind as string,
            happened_on: (e.happened_on as string) ?? null,
            due_on: (e.due_on as string) ?? null,
            percent: num(e.percent),
          })),
        };
      });

  const copies: Copy[] = r.copies.map((c) => {
    const cp = one<Row>(c.copy_private);
    return {
      id: c.id as string,
      edition_id: (c.edition_id as string) ?? null,
      format: c.format as Copy['format'],
      how: c.how as Copy['how'],
      state: c.state as Copy['state'],
      acquired_on: (c.acquired_on as string) ?? null,
      acquired_precision: (c.acquired_precision as Copy['acquired_precision']) ?? null,
      acquired_words: (cp?.acquired_words as string) ?? null,
      store: name(c.store) || null,
      pages: num(c.pages),
      listening_minutes: num(c.listening_minutes),
      note: (cp?.note as string) ?? null,
      // Two decimals always: $13.50, not $13.5 (Kristi, Oct 27).
      price: cp?.price === null || cp?.price === undefined ? null : Number(cp.price).toFixed(2),
      found_through: (cp?.found_through as string) ?? null,
      got_from: (cp?.got_from as string) ?? null,
      room: (one<Row>(cp?.room)?.name as string) ?? null,
      shelf: (cp?.shelf_name as string) ?? null,
      lent_to: (cp?.lent_to as string) ?? null,
      lent_on: (cp?.lent_on as string) ?? null,
      link: (r.links.find((l) => l.copy_id === c.id && l.kind !== 'library_kindle')?.url as string) ?? null,
      library_kindle_url: (r.links.find((l) => l.copy_id === c.id && l.kind === 'library_kindle')?.url as string) ?? null,
      signed: c.signed === true,
      number_in_run: num(c.number_in_run),
      run_size: num(c.run_size),
      loans: loansFor(c.id as string),
    };
  });

  const reads: Read[] = r.reads.map((x) => {
    const rp = one<Row>(x.read_private);
    return {
      id: x.id as string,
      started_on: (x.started_on as string) ?? null,
      started_precision: (x.started_precision as Read['started_precision']) ?? null,
      started_words: (rp?.started_words as string) ?? null,
      started_at_percent: num(x.started_at_percent),
      outcome: x.outcome as Read['outcome'],
      ended_on: (x.ended_on as string) ?? null,
      ended_precision: (x.ended_precision as Read['ended_precision']) ?? null,
      ended_words: (rp?.ended_words as string) ?? null,
      stopped_percent: num(x.stopped_percent),
      stop_chips: (x.stop_chips as string[]) ?? [],
      stop_reason: (rp?.stop_reason as string) ?? null,
      rating: num(x.rating),
      reading_as: (x.reading_as as Read['reading_as']) ?? null,
      created_at: (x.created_at as string) ?? '',
      copies: many<Row>(x.read_copies).map((c) => ({
        copy_id: c.copy_id as string,
        added_on: (c.added_on as string) ?? null,
        stopped_using_on: (c.stopped_using_on as string) ?? null,
      })),
      pauses: many<Row>(x.pauses).map((q) => ({
        chips: (q.chips as string[] | null) ?? [],
        paused_on: (q.paused_on as string) ?? null,
        resumed_on: (q.resumed_on as string) ?? null,
        reason: (one<Row>(q.pause_private)?.reason as string) ?? null,
      })),
      progress: many<Row>(x.progress_updates).map((q) => ({ noted_at: q.noted_at as string, percent: Number(q.percent) })),
      takeaways: many<Row>(x.takeaways)
        .sort((a, c) => Number(a.sort_order) - Number(c.sort_order))
        .map((t) => t.body as string),
    };
  });

  const series: SeriesPlace[] = r.places.map((s) => ({
    id: (s.series_id as string) ?? undefined,
    name: name(s.series),
    position: num(s.position),
    label: (s.label as string) ?? null,
  }));

  const sameSeries: SeriesBook[] = r.seriesBooks
    .map((sb) => ({
      label: Number(sb.label_number),
      title: (one<Row>(sb.work)?.title as string) ?? '',
      status: (sb.status as Status) ?? null,
      position: num(r.seriesWorks.find((w) => w.work_id === sb.work_id)?.position),
      cover: ((r.seriesCovers ?? []).find((c) => c.work_id === sb.work_id)?.cover_url as string) ?? null,
      owned: ownsOne(r.otherCopies, sb.id),
      author: firstAuthorOf(r.otherPeople, sb.work_id),
    }))
    .sort((a, c) => (a.position ?? 9e9) - (c.position ?? 9e9));

  const genreName = (x: unknown) => {
    const g = one<Row>(x);
    return ((g?.short_name as string) || (g?.name as string)) ?? '';
  };

  const subgenreOf = (id: unknown) => genreName(r.subgenres.find((g) => g.subgenre_id === id)?.subgenre);
  const sameAuthor: SimilarBook[] = (r.authorBooks ?? []).map((x) => ({
    label: Number(x.label_number),
    title: (one<Row>(x.work)?.title as string) ?? '',
    status: (x.status as Status) ?? null,
    cover: ((r.seriesCovers ?? []).find((c) => c.work_id === x.work_id)?.cover_url as string) ?? null,
    subgenre: null,
    owned: ownsOne(r.otherCopies, x.id),
    author: firstAuthorOf(r.otherPeople, x.work_id),
  }));
  const sameSubgenre: SimilarBook[] = (r.similarBooks ?? []).map((x) => ({
    label: Number(x.label_number),
    title: (one<Row>(x.work)?.title as string) ?? '',
    status: (x.status as Status) ?? null,
    cover: ((r.seriesCovers ?? []).find((c) => c.work_id === x.work_id)?.cover_url as string) ?? null,
    subgenre: subgenreOf((r.subgenreMates ?? []).find((m) => m.book_id === x.id)?.subgenre_id) || null,
    owned: ownsOne(r.otherCopies, x.id),
    author: firstAuthorOf(r.otherPeople, x.work_id),
  }));

  return {
    id: b.id as string,
    label: Number(b.label_number),
    status: (b.status as Status) ?? null,
    readType: rt ? { name: rt.name as string, description: (rt.description as string) ?? null } : null,
    interest: num(b.interest),
    favoriteAt: (b.favorite_at as string) ?? null,
    readAgainAt: (b.read_again_at as string) ?? null,
    tryAgainAt: (b.try_again_at as string) ?? null,
    notForMe: {
      kind: (b.not_for_me_kind as BookData['notForMe']['kind']) ?? null,
      on: (b.not_for_me_on as string) ?? null,
      where: (b.not_for_me_where as string) ?? null,
      chips: (b.not_for_me_chips as string[] | null) ?? [],
    },
    addedAt: (b.added_at as string) ?? null,
    addedHow: (b.added_how as string) ?? null,
    lastReviewedAt: (b.last_reviewed_at as string) ?? null,
    whyRead: (p.why_read as string) ?? null,
    recommendedBy: (p.recommended_by as string) ?? null,
    notes: (p.notes as string) ?? null,
    work: {
      id: (b.work_id as string) ?? (work.id as string),
      title: work.title as string,
      subtitle: (work.subtitle as string) ?? null,
      first_published_on: (work.first_published_on as string) ?? null,
      first_published_precision: (work.first_published_precision as BookData['work']['first_published_precision']) ?? null,
      first_published_words: (work.first_published_words as string) ?? null,
      summary: (work.summary as string) ?? null,
      summary_source: (work.summary_source as string) ?? null,
      fiction: (work.fiction as BookData['work']['fiction']) ?? null,
      audience: (work.audience as BookData['work']['audience']) ?? null,
      goodreads_score: num(work.goodreads_score),
      goodreads_checked_on: (work.goodreads_checked_on as string) ?? null,
      goodreads_ratings_count: num(work.goodreads_ratings_count),
      goodreads_id: (work.goodreads_id as string) ?? null,
      for_fans_of: (work.for_fans_of as string) ?? null,
    },
    authors: r.people.filter((x) => x.role === 'author' || x.role === 'co_author').map((x) => name(x.person)).filter(Boolean),
    authorLinks: r.people
      .filter((x) => (x.role === 'author' || x.role === 'co_author') && x.person_id && name(x.person))
      .map((x) => ({ id: x.person_id as string, name: name(x.person) })),
    series,
    editions,
    copies,
    reads,
    // Hidden genres (Young Adult, Children's Books since 0035: audience carries them) aren't shown; kept for saving.
    genres: r.genres.filter((g) => !(g.genre as Row | null)?.hidden).map((g) => genreName(g.genre)).filter(Boolean),
    hiddenGenres: r.genres.filter((g) => (g.genre as Row | null)?.hidden).map((g) => genreName(g.genre)).filter(Boolean),
    subgenres: r.subgenres.map((g) => genreName(g.subgenre)).filter(Boolean),
    tags: r.tags
      .map((t) => one<Row>(t.tag))
      .filter((t): t is Row => Boolean(t))
      .sort((a, c) => Number(a.sort_order) - Number(c.sort_order))
      .map((t) => t.name as string),
    kindleLeftOut: (r.leftOut ?? []).map((x) => x.kindle_key as string),
    coverLink: (r.links.find((l) => !l.copy_id && l.label === 'Cover')?.url as string) ?? null,
    readingNotes: r.notes as unknown as Note[],
    quotes: r.quotes as unknown as Quote[],
    history: r.history as unknown as HistoryRow[],
    honours: sortHonours(
      (r.honours ?? []).flatMap((h) => {
        const ed = one<Row>(h.edition);
        const l = one<Row>(ed?.list);
        if (!ed || !l) return [];
        return [{ listId: l.id as string, list: l.name as string, year: Number(ed.year), label: (ed.label as string) ?? null, level: (h.level as string) ?? null, category: (h.category as string) ?? null, rank: h.rank === null || h.rank === undefined ? null : Number(h.rank), sortOrder: Number(l.sort_order ?? 0) }];
      }),
    ),
    myLists: r.lists.map((l) => (l.year ? `${l.name as string} (${l.year as number})` : (l.name as string))),
    sameSeries,
    sameSubgenre,
    sameAuthor,
    libbyCode: (r.libraries[0]?.libby_code as string) ?? null,
    keptShelves: (r.positions ?? []).map((p) => p.shelf as Status),
    readTypes: (r.readTypes ?? []).map((t) => ({ name: t.name as string, description: (t.description as string) ?? null })),
  };
}

/** Header search (until the search page exists): books on your list by title, author or B-number. */
export interface FoundBook {
  label: number;
  title: string;
  status: Status | null;
}

export async function findBooks(text: string): Promise<FoundBook[]> {
  const db = supabase;
  const q = text.trim().replace(/[%_*,()\\]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!db || q.length < 2) return [];
  const asLabel = q.match(/^b?0*(\d{1,6})$/i);
  const pick = 'label_number, status, work:works!inner(title)';
  const [byLabel, byTitle, byAuthor] = await Promise.all([
    asLabel ? rows(db.from('books').select(pick).eq('label_number', Number(asLabel[1])).limit(1)) : Promise.resolve([] as Row[]),
    rows(db.from('books').select(pick).ilike('work.title', `%${q}%`).limit(8)),
    rows(db.from('people').select('id').ilike('name', `%${q}%`).limit(10)).then(async (ppl) => {
      if (!ppl.length) return [] as Row[];
      const wp = await rows(db.from('work_people').select('work_id').in('person_id', ppl.map((x) => x.id as string)).limit(40));
      if (!wp.length) return [] as Row[];
      return rows(db.from('books').select(pick).in('work_id', [...new Set(wp.map((w) => w.work_id as string))]).limit(8));
    }),
  ]);
  const seen = new Set<number>();
  const out: FoundBook[] = [];
  for (const x of [...byLabel, ...byTitle, ...byAuthor]) {
    const label = Number(x.label_number);
    if (seen.has(label)) continue;
    seen.add(label);
    out.push({ label, title: (one<Row>(x.work)?.title as string) ?? '', status: (x.status as Status) ?? null });
    if (out.length === 8) break;
  }
  return out;
}
